import { useEffect, useState } from 'react';

/**
 * Tailwind's `xl`, in JavaScript.
 *
 * Almost everything responsive in this app is a class, which is where it
 * belongs: one layout, described once, with no component asking how wide the
 * window is. This is the exception. Below `xl` the detail pane is a modal sheet
 * and above it the same element is a column beside the list, and modality is not
 * a thing CSS can say -- `role="dialog"`, the focus that moves into it, the
 * Escape key and the body scroll lock are all decisions a component has to make.
 *
 * The number is Tailwind's default `xl` breakpoint. It is duplicated here for
 * want of anywhere to import it from, so a theme that retunes the breakpoints
 * has to retune this line too.
 */
const WIDE = '(min-width: 80rem)';

export function useWideLayout(): boolean {
  const [wide, setWide] = useState(() => window.matchMedia(WIDE).matches);

  useEffect(() => {
    const query = window.matchMedia(WIDE);
    const onChange = () => setWide(query.matches);
    // Also read once now: a window resized between the first render and this
    // effect would otherwise leave the answer stuck at whatever it was.
    onChange();
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return wide;
}

/**
 * Tailwind's `lg`, in JavaScript, and for one caller: the Steps board.
 *
 * The same exception the hook above is. Lanes and a stacked list are not two
 * layouts of one tree, they are two trees: the board is five scrollers with a
 * FLIP animation between them and the list is five sections in the page's own
 * scroll. Drawing both and hiding one with a class would mount two copies of
 * every row, and the roving focus the page installs would have two elements
 * claiming each id.
 *
 * `lg` rather than `xl` because a five-lane board wants less width than the
 * detail pane beside it does: at 1024 the lanes are still wide enough to read a
 * title in, and below that they are five columns of one card, which is worse
 * than the list at every width where it would fit.
 */
const BOARD = '(min-width: 64rem)';

export function useBoardLayout(): boolean {
  const [board, setBoard] = useState(() => window.matchMedia(BOARD).matches);

  useEffect(() => {
    const query = window.matchMedia(BOARD);
    const onChange = () => setBoard(query.matches);
    onChange();
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return board;
}
